import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User as FirebaseUser,
  UserCredential,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
  signInAnonymously,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  linkWithPopup,
  linkWithCredential,
  unlink,
} from 'firebase/auth';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { GoogleAuth } from '@/plugins/GoogleAuth';

// Apple Auth plugin interface
interface AppleAuthPlugin {
  signIn(): Promise<{
    identityToken: string;
    rawNonce: string;
    user: string;
    email?: string;
    fullName?: string;
    authorizationCode?: string;
  }>;
}

// Register native Apple Auth plugin
const AppleAuth = registerPlugin<AppleAuthPlugin>('AppleAuth');
import { doc, getDoc, setDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '@/lib/firebase';
import { User, CustomClaims } from '@/types';
import { parseCustomClaims, parseUser } from '@/lib/validation';
import {
  initializeMessaging,
  getFCMToken,
  deleteFCMToken,
  clearStaleTokens,
  onForegroundMessage,
  isNativePlatform,
  initializeNativePush
} from '@/services/notificationService';
import { saveTokenViaNativePlugin } from '@/lib/pushNotifications';
import { analyticsService } from '@/services/analyticsService';
import { logEvent } from 'firebase/analytics';
import { analytics } from '@/lib/firebase';
import { getFranchiseIdFromUrl } from '@/config/franchiseUrls';

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  customClaims: CustomClaims | null;
  loading: boolean;
  emailVerified: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInAsGuest: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<UserCredential>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<UserCredential>;
  upgradeGuestAccount: () => Promise<void>;
  upgradeGuestAccountWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  linkGoogleAccount: () => Promise<{ success: boolean }>;
  unlinkGoogleAccount: () => Promise<{ success: boolean }>;
  updateUserFranchise: (franchiseId: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  refreshEmailVerification: () => Promise<boolean>;
  // Welcome stamp modal (replaces old welcome points)
  showWelcomeModal: boolean;
  dismissWelcomeModal: () => Promise<void>;
  // Guest name modal (ask name immediately on guest login)
  showGuestNameModal: boolean;
  dismissGuestNameModal: () => void;
  updateGuestName: (name: string, phone: string) => Promise<void>;
  /** Sync local user state after contact info was written elsewhere (e.g. the
   *  phone prompt before booking), so we don't re-prompt on the next visit. */
  updateUserContact: (partial: { displayName?: string; phoneNumber?: string }) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Obtiene el franchiseId desde múltiples fuentes en orden de prioridad
 *
 * PRIORITY ORDER:
 * 1. URL hostname mapping (HIGHEST PRIORITY - e.g., monkeybarber.club → franchiseId)
 * 2. localStorage pendingFranchiseId
 * 3. sessionStorage currentFranchiseId
 * 4. URL query parameter ?franchiseId=xxx
 * 5. URL path /franchise/xxx
 */
function getFranchiseIdFromSources(): string | undefined {
  // 1. HIGHEST PRIORITY: Get franchiseId from URL hostname mapping
  // This ensures users accessing from a franchise-specific domain
  // are automatically associated with that franchise
  const urlFranchiseId = getFranchiseIdFromUrl();
  if (urlFranchiseId) {
    console.log(`✅ FranchiseId from URL hostname: ${urlFranchiseId}`);
    return urlFranchiseId;
  }

  // 2. Try localStorage (user navigating specific franchise)
  const pendingFranchiseId = localStorage.getItem('pendingFranchiseId');
  if (pendingFranchiseId) {
    console.log(`✅ FranchiseId from localStorage: ${pendingFranchiseId}`);
    return pendingFranchiseId;
  }

  // 3. Try sessionStorage (temporary navigation)
  const sessionFranchiseId = sessionStorage.getItem('currentFranchiseId');
  if (sessionFranchiseId) {
    console.log(`✅ FranchiseId from sessionStorage: ${sessionFranchiseId}`);
    return sessionFranchiseId;
  }

  // 4. Try URL params if on a franchise page
  const urlParams = new URLSearchParams(window.location.search);
  const queryFranchiseId = urlParams.get('franchiseId');
  if (queryFranchiseId) {
    // Save to sessionStorage to persist during session
    sessionStorage.setItem('currentFranchiseId', queryFranchiseId);
    console.log(`✅ FranchiseId from URL query: ${queryFranchiseId}`);
    return queryFranchiseId;
  }

  // 5. Try path with franchiseId (e.g., /franchise/xxx)
  const pathMatch = window.location.pathname.match(/\/franchise\/([^\/]+)/);
  if (pathMatch && pathMatch[1]) {
    const pathFranchiseId = pathMatch[1];
    sessionStorage.setItem('currentFranchiseId', pathFranchiseId);
    console.log(`✅ FranchiseId from URL path: ${pathFranchiseId}`);
    return pathFranchiseId;
  }

  console.warn('⚠️ No franchiseId found from any source');
  console.warn('⚠️ User may see data from ALL franchises!');
  return undefined;
}

// Module-level variable to pass Apple/social displayName to createUserDocument
// This avoids race conditions between signInWithApple's setDoc and onAuthStateChanged
let pendingDisplayName: string | null = null;

/**
 * Crea el documento de usuario en Firestore cuando se registra por primera vez
 */
async function createUserDocument(firebaseUser: FirebaseUser): Promise<void> {
  const isAnonymous = firebaseUser.providerData.length === 0;
  const defaultRole = isAnonymous ? 'guest' : 'client';

  // Obtener franchiseId desde múltiples fuentes
  const franchiseId = getFranchiseIdFromSources();

  // Datos básicos del usuario - NO incluir queuePoints, eso lo maneja el trigger beforeUserCreated
  const userData = {
    id: firebaseUser.uid,
    email: firebaseUser.email || null,
    displayName: pendingDisplayName || firebaseUser.displayName || (isAnonymous ? 'Invitado' : (firebaseUser.email ? firebaseUser.email.split('@')[0] : 'Usuario')),
    photoURL: firebaseUser.photoURL || null,
    phoneNumber: firebaseUser.phoneNumber || null,
    role: defaultRole,
    isAnonymous,
    ...(franchiseId && { franchiseId }), // Asignar franchiseId si existe
    queueHistory: {
      totalCompleted: 0,
      totalNoShows: 0,
      totalExpired: 0,
      totalCancelled: 0,
    },
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  // Usar merge: true para no sobrescribir queuePoints que el trigger establece
  await setDoc(doc(db, 'users', firebaseUser.uid), userData, { merge: true });

  // Limpiar pendingDisplayName después de crear el documento
  pendingDisplayName = null;

  // Limpiar el pendingFranchiseId del localStorage después de usarlo
  if (franchiseId === localStorage.getItem('pendingFranchiseId')) {
    localStorage.removeItem('pendingFranchiseId');
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [customClaims, setCustomClaims] = useState<CustomClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentFCMToken, setCurrentFCMToken] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(true); // true por defecto para Google/guest
  // Welcome stamp modal state (replaces old welcome points)
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  // Guest name modal state (ask name immediately on guest login)
  const [showGuestNameModal, setShowGuestNameModal] = useState(false);

  // Initialize FCM messaging on mount
  useEffect(() => {
    initializeMessaging();
  }, []);

  // Setup foreground notification handler
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('Notification received in foreground:', payload);
      // You can show a toast notification or update UI here
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Escuchar cambios en el estado de autenticación
  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (cancelled) return;

      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        try {
          // Obtener custom claims con validación Zod
          const tokenResult = await firebaseUser.getIdTokenResult(true);
          if (cancelled) return;

          const validatedClaims = parseCustomClaims(tokenResult.claims);
          if (validatedClaims) {
            // Usar claims validados, pero llenar valores faltantes con defaults
            setCustomClaims({
              role: validatedClaims.role || (firebaseUser.isAnonymous ? 'guest' : 'client'),
              isAnonymous: validatedClaims.isAnonymous ?? firebaseUser.isAnonymous,
              franchiseId: validatedClaims.franchiseId,
              branchId: validatedClaims.branchId,
            });
          } else {
            // Si la validación falla completamente, usar defaults
            console.warn('Custom claims validation failed, using defaults');
            setCustomClaims({
              role: firebaseUser.isAnonymous ? 'guest' : 'client',
              isAnonymous: firebaseUser.isAnonymous,
            });
          }

          // Obtener datos del usuario desde Firestore con validación Zod
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (cancelled) return;

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Check if account is soft-deleted
            if (userData?.isDeleted === true) {
              console.warn('Account is deleted, signing out');
              await firebaseSignOut(auth);
              setUser(null);
              setCustomClaims(null);
              setLoading(false);
              return;
            }

            const validatedUser = parseUser(userData);
            if (validatedUser) {
              setUser(validatedUser as User);
            } else {
              console.error('Invalid user data from Firestore');
              setUser(null);
            }

            // Check if we need to show welcome stamp modal (new system)
            // Also support old welcome points for backward compatibility
            const hasNewWelcomeStamp = userData?.welcomeStampAwarded && !userData?.welcomeStampShown;
            const hasOldWelcomePoints = userData?.welcomePointsAwarded && !userData?.welcomePointsShown && userData?.welcomePointsAmount > 0;
            if (hasNewWelcomeStamp || hasOldWelcomePoints) {
              setShowWelcomeModal(true);
            }

            // Verificar si el email está verificado desde Firestore
            // Google, Apple y anónimos siempre se consideran verificados
            const isSocialUser = firebaseUser.providerData.some(
              p => p.providerId === 'google.com' || p.providerId === 'apple.com'
            );
            const needsVerification = !firebaseUser.isAnonymous && !isSocialUser && firebaseUser.email;
            if (needsVerification) {
              setEmailVerified(userData?.emailVerified === true);
            } else {
              setEmailVerified(true);
            }
          } else {
            // Si el documento no existe, crearlo (primera vez)
            await createUserDocument(firebaseUser);
            if (cancelled) return;

            // For new users with social login (Google/Apple), wait for the onUserRegistered
            // Cloud Function trigger to add the welcome stamp flag (~500-1000ms)
            const isSocialLogin = firebaseUser.providerData.some(
              p => p.providerId === 'google.com' || p.providerId === 'apple.com'
            );

            // First read - get user data immediately
            let newUserDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (cancelled) return;

            if (newUserDoc.exists()) {
              let newUserData = newUserDoc.data();
              const validatedNewUser = parseUser(newUserData);
              if (validatedNewUser) {
                setUser(validatedNewUser as User);
              }

              // For social login, if welcome stamp not yet awarded, use real-time listener
              // This is more reliable than polling, especially on slow mobile networks
              // The onUserRegistered Cloud Function will set welcomeStampAwarded: true
              if (isSocialLogin && !newUserData?.welcomeStampAwarded && !firebaseUser.isAnonymous) {
                console.log('[AuthContext] New social login user, setting up welcome stamp listener...');
                console.log('[AuthContext] Platform:', Capacitor.getPlatform(), 'isNative:', Capacitor.isNativePlatform());

                // Use onSnapshot to listen for real-time updates instead of polling
                // This handles Cloud Function cold starts and slow mobile networks
                await new Promise<void>((resolve) => {
                  const timeoutMs = Capacitor.isNativePlatform() ? 15000 : 10000; // 15s mobile, 10s web
                  let resolved = false;

                  // Safety timeout - resolve even if stamp never arrives
                  const timeoutId = setTimeout(() => {
                    if (!resolved) {
                      resolved = true;
                      console.log('[AuthContext] Welcome stamp listener timeout, continuing without stamp');
                      unsubscribeStamp();
                      resolve();
                    }
                  }, timeoutMs);

                  // Listen for document changes
                  const unsubscribeStamp = onSnapshot(
                    doc(db, 'users', firebaseUser.uid),
                    (docSnapshot) => {
                      if (resolved || cancelled) return;

                      const data = docSnapshot.data();
                      console.log('[AuthContext] User doc update, welcomeStampAwarded:', data?.welcomeStampAwarded);

                      if (data?.welcomeStampAwarded) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        unsubscribeStamp();

                        // Update user data with the stamp
                        newUserData = data;
                        const revalidatedUser = parseUser(data);
                        if (revalidatedUser) {
                          setUser(revalidatedUser as User);
                        }

                        resolve();
                      }
                    },
                    (error) => {
                      console.error('[AuthContext] Welcome stamp listener error:', error);
                      if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        resolve();
                      }
                    }
                  );
                });
                if (cancelled) return;
              }

              // Check if we need to show welcome stamp modal for new user (new system)
              // Also support old welcome points for backward compatibility
              const hasNewWelcomeStamp = newUserData?.welcomeStampAwarded && !newUserData?.welcomeStampShown;
              const hasOldWelcomePoints = newUserData?.welcomePointsAwarded && !newUserData?.welcomePointsShown && newUserData?.welcomePointsAmount > 0;
              if (hasNewWelcomeStamp || hasOldWelcomePoints) {
                setShowWelcomeModal(true);
              }

              // Nuevo usuario - email no verificado por defecto
              // Google and Apple users are considered verified (OAuth providers verify email)
              const isSocialUser = firebaseUser.providerData.some(
                p => p.providerId === 'google.com' || p.providerId === 'apple.com'
              );
              const needsVerification = !firebaseUser.isAnonymous && !isSocialUser && firebaseUser.email;
              console.log('[AuthContext] New user emailVerified check:', {
                isAnonymous: firebaseUser.isAnonymous,
                isSocialUser,
                needsVerification,
                firestoreEmailVerified: newUserData?.emailVerified,
              });
              if (needsVerification) {
                setEmailVerified(newUserData?.emailVerified === true);
              } else {
                setEmailVerified(true);
              }
            }
          }

          // Register FCM token for authenticated users (not guests)
          if (!firebaseUser.isAnonymous && !cancelled) {
            try {
              // For native iOS, use the native plugin to save token via HTTP
              if (isNativePlatform() && Capacitor.getPlatform() === 'ios') {
                console.log('[AuthContext] iOS detected, initializing native push...');
                // First initialize native push to get permission and token
                const nativeToken = await initializeNativePush();
                console.log('[AuthContext] Native token result:', nativeToken ? nativeToken.substring(0, 30) + '...' : 'null');

                if (nativeToken) {
                  console.log('[AuthContext] Native token obtained, trying to save...');

                  // Try native plugin first
                  let saved = false;
                  try {
                    saved = await saveTokenViaNativePlugin(firebaseUser.uid);
                    console.log('[AuthContext] Native plugin save result:', saved);
                  } catch (pluginError) {
                    console.error('[AuthContext] Native plugin failed:', pluginError);
                  }

                  // If plugin failed, try direct HTTP call as fallback
                  if (!saved) {
                    console.log('[AuthContext] Trying direct HTTP fallback...');
                    try {
                      // La URL se deriva del proyecto configurado, no se fija a mano:
                      // con dos entornos, un literal 'monkeybarberapp' aquí haría que
                      // una build de dev guardase el token FCM en PRODUCCIÓN.
                      const proyecto = import.meta.env.VITE_FIREBASE_PROJECT_ID;
                      const urlSaveToken = `https://europe-west1-${proyecto}.cloudfunctions.net/saveFCMTokenHTTP`;
                      const response = await fetch(urlSaveToken, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          userId: firebaseUser.uid,
                          token: nativeToken,
                          platform: 'ios'
                        })
                      });
                      const result = await response.json();
                      console.log('[AuthContext] HTTP fallback result:', result);
                      saved = result.success;
                    } catch (httpError) {
                      console.error('[AuthContext] HTTP fallback failed:', httpError);
                    }
                  }

                  if (saved) {
                    setCurrentFCMToken(nativeToken);
                    console.log('[AuthContext] ✅ FCM token saved successfully');
                    // Clear this token from any other users (shared device)
                    clearStaleTokens(firebaseUser.uid, nativeToken);
                  }
                }
              } else {
                // For web/Android, use existing method
                const token = await getFCMToken(firebaseUser.uid);
                if (token && !cancelled) {
                  setCurrentFCMToken(token);
                  console.log('FCM token registered for user');
                  // Clear this token from any other users (shared device)
                  clearStaleTokens(firebaseUser.uid, token);
                }
              }
            } catch (error) {
              console.error('Error registering FCM token:', error);
              // Non-critical error, don't block user flow
            }
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          if (!cancelled) {
            setUser(null);
            setCustomClaims(null);
          }
        }
      } else {
        // User signed out - delete FCM token
        // Note: firebaseUser is null here, so we can't delete the token
        // Tokens will be cleaned up by Cloud Functions when they become invalid
        setUser(null);
        setCustomClaims(null);
        setCurrentFCMToken(null);
        setEmailVerified(true); // Reset para el siguiente usuario
      }

      if (!cancelled) {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Don't include currentFCMToken - it would cause re-subscription on login

  const signInWithGoogle = async () => {
    try {
      let result;

      // Check if running on native platform (Android/iOS)
      if (Capacitor.isNativePlatform()) {
        // Use native Google Sign-In
        await GoogleAuth.initialize();
        const googleUser = await GoogleAuth.signIn({ scopes: ['profile', 'email'] }) as any;

        // Create Firebase credential from Google ID token
        const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);

        // Sign in to Firebase with the credential
        result = await signInWithCredential(auth, credential);
      } else {
        // Use web popup for browser
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
          prompt: 'select_account',
        });
        result = await signInWithPopup(auth, provider);
      }

      // Track login
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
      if (isNewUser) {
        analyticsService.trackSignUp('google', customClaims?.role);
      } else {
        analyticsService.trackLogin('google', customClaims?.role);
      }
    } catch (error: any) {
      // Handle account-exists-with-different-credential error
      if (error.code === 'auth/account-exists-with-different-credential') {
        // Get the pending credential
        const pendingCred = GoogleAuthProvider.credentialFromError(error);
        const email = error.customData?.email;

        if (email && pendingCred) {
          throw new Error(
            `Ya existe una cuenta con el email ${email}. Por favor, inicia sesión con tu método existente y vincula Google desde tu perfil.`
          );
        }
      }
      throw error;
    }
  };

  const signInWithApple = async () => {
    try {
      // Apple Sign-In is only available on iOS native
      if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
        throw new Error('Apple Sign-In solo está disponible en iOS');
      }

      // Call native Apple Auth plugin
      const appleUser = await AppleAuth.signIn();

      // Create Firebase OAuth credential with Apple provider
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: appleUser.identityToken,
        rawNonce: appleUser.rawNonce,
      });

      // Store Apple displayName BEFORE signInWithCredential fires onAuthStateChanged
      // This avoids a race condition where onAuthStateChanged creates a partial doc
      pendingDisplayName =
        appleUser.fullName ||
        (appleUser.email ? appleUser.email.split('@')[0] : null) ||
        null;

      // Sign in to Firebase with the credential (triggers onAuthStateChanged)
      const result = await signInWithCredential(auth, credential);

      // For returning users (doc already exists with all fields), update displayName if Apple provided one
      // For NEW users, createUserDocument handles it via pendingDisplayName (no partial doc race condition)
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
      if (!isNewUser && pendingDisplayName) {
        await setDoc(doc(db, 'users', result.user.uid), {
          displayName: pendingDisplayName,
          updatedAt: Timestamp.now(),
        }, { merge: true });
        pendingDisplayName = null;
      }

      if (isNewUser) {
        analyticsService.trackSignUp('apple', customClaims?.role);
      } else {
        analyticsService.trackLogin('apple', customClaims?.role);
      }
    } catch (error: any) {
      // Handle user cancellation gracefully
      if (error.code === 'USER_CANCELLED' || error.message?.includes('cancelled')) {
        throw new Error('Inicio de sesión cancelado');
      }

      // Handle account-exists-with-different-credential error
      if (error.code === 'auth/account-exists-with-different-credential') {
        const email = error.customData?.email;
        if (email) {
          throw new Error(
            `Ya existe una cuenta con el email ${email}. Por favor, inicia sesión con tu método existente.`
          );
        }
      }
      throw error;
    }
  };

  const signInAsGuest = async () => {
    await signInAnonymously(auth);
    analyticsService.trackLogin('anonymous', 'guest');
    // Show guest name modal immediately after guest login
    setShowGuestNameModal(true);
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Track login
      analyticsService.trackLogin('email', customClaims?.role);

      return result;
    } catch (error: any) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Obtener franchiseId desde múltiples fuentes
      const franchiseId = getFranchiseIdFromSources();

      // Update user document - NO incluir queuePoints, eso lo maneja el trigger beforeUserCreated
      // emailVerified: false por defecto para usuarios de email/password
      await setDoc(doc(db, 'users', result.user.uid), {
        id: result.user.uid,
        email: result.user.email,
        displayName,
        photoURL: null,
        phoneNumber: null,
        role: 'client', // Default role for email signup
        isAnonymous: false,
        emailVerified: false, // Necesita verificar email
        ...(franchiseId && { franchiseId }), // Asignar franchiseId si existe
        queueHistory: {
          totalCompleted: 0,
          totalNoShows: 0,
          totalExpired: 0,
          totalCancelled: 0,
        },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      }, { merge: true });

      setEmailVerified(false); // El usuario aún no ha verificado su email

      // Enviar email de verificación usando Resend (Cloud Function)
      try {
        const sendVerificationEmailFn = httpsCallable(functions, 'sendVerificationEmail');
        await sendVerificationEmailFn({});
      } catch (emailError) {
        console.error('Error sending verification email:', emailError);
        // No bloquear el registro si falla el envío del email
      }

      // Limpiar el pendingFranchiseId del localStorage después de usarlo
      if (franchiseId === localStorage.getItem('pendingFranchiseId')) {
        localStorage.removeItem('pendingFranchiseId');
      }

      // Track signup
      analyticsService.trackSignUp('email', 'client');

      return result;
    } catch (error: any) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  const upgradeGuestAccount = async () => {
    if (!firebaseUser || !firebaseUser.isAnonymous) {
      throw new Error('Usuario no es invitado o no está autenticado');
    }

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
    });
    await linkWithPopup(firebaseUser, provider);

    // Track account upgrade
    analyticsService.trackSignUp('google_upgrade', customClaims?.role);
  };

  const upgradeGuestAccountWithApple = async () => {
    if (!firebaseUser || !firebaseUser.isAnonymous) {
      throw new Error('Usuario no es invitado o no está autenticado');
    }

    // Apple Sign-In is only available on iOS native
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      throw new Error('Apple Sign-In solo está disponible en iOS');
    }

    try {
      // Call native Apple Auth plugin
      const appleUser = await AppleAuth.signIn();

      // Create Firebase OAuth credential with Apple provider
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: appleUser.identityToken,
        rawNonce: appleUser.rawNonce,
      });

      // Link the anonymous account with Apple credential
      const result = await linkWithCredential(firebaseUser, credential);

      // Build a display name: Apple fullName > existing name > email prefix > fallback
      const displayName =
        appleUser.fullName ||
        result.user.displayName ||
        (appleUser.email ? appleUser.email.split('@')[0] : null) ||
        (result.user.email ? result.user.email.split('@')[0] : null) ||
        'Usuario Apple';

      // Always update the user doc with the best displayName available
      await setDoc(doc(db, 'users', result.user.uid), {
        displayName,
        isAnonymous: false,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      // Track account upgrade
      analyticsService.trackSignUp('apple_upgrade', customClaims?.role);
    } catch (error: any) {
      // Handle user cancellation gracefully
      if (error.code === 'USER_CANCELLED' || error.message?.includes('cancelled')) {
        throw new Error('Vinculación cancelada');
      }

      // Handle credential already in use
      if (error.code === 'auth/credential-already-in-use') {
        throw new Error('Esta cuenta de Apple ya está vinculada a otro usuario');
      }

      throw error;
    }
  };

  const signOut = async () => {
    // Track logout
    analyticsService.trackLogout();

    // Delete FCM token before signing out
    if (currentFCMToken && firebaseUser) {
      try {
        await deleteFCMToken(firebaseUser.uid, currentFCMToken);
        console.log('FCM token deleted on sign out');
      } catch (error) {
        console.error('Error deleting FCM token:', error);
      }
    }
    await firebaseSignOut(auth);
  };

  const linkGoogleAccount = async () => {
    if (!firebaseUser) {
      throw new Error('No user logged in');
    }

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account',
      });
      const result = await linkWithPopup(firebaseUser, provider);

      // Update user document with Google provider info
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        photoURL: result.user.photoURL,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      if (analytics) {
        logEvent(analytics, 'account_linked', { provider: 'google' });
      }
      return { success: true };
    } catch (error: any) {
      if (error.code === 'auth/credential-already-in-use') {
        throw new Error('Esta cuenta de Google ya está vinculada a otro usuario');
      }
      if (error.code === 'auth/provider-already-linked') {
        throw new Error('Esta cuenta ya tiene Google vinculado');
      }
      throw error;
    }
  };

  const unlinkGoogleAccount = async () => {
    if (!firebaseUser) {
      throw new Error('No user logged in');
    }

    try {
      await unlink(firebaseUser, 'google.com');
      if (analytics) {
        logEvent(analytics, 'account_unlinked', { provider: 'google' });
      }
      return { success: true };
    } catch (error: any) {
      if (error.code === 'auth/no-such-provider') {
        throw new Error('No hay cuenta de Google vinculada');
      }
      throw error;
    }
  };

  const updateUserFranchise = async (franchiseId: string) => {
    if (!firebaseUser) {
      throw new Error('No user logged in');
    }

    try {
      // Actualizar el documento del usuario en Firestore
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        franchiseId,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      // Guardar en sessionStorage para la sesión actual
      sessionStorage.setItem('currentFranchiseId', franchiseId);

      // También actualizar en localStorage para persistencia
      localStorage.setItem('pendingFranchiseId', franchiseId);

      // Forzar recarga del token para actualizar custom claims si es necesario
      if (firebaseUser) {
        await firebaseUser.getIdToken(true);
      }

      // Actualizar el estado local del usuario
      setUser(prev => prev ? { ...prev, franchiseId } : null);

      // Track el cambio de franquicia
      if (analytics) {
        logEvent(analytics, 'franchise_changed', { franchiseId });
      }
    } catch (error) {
      console.error('Error updating user franchise:', error);
      throw error;
    }
  };

  const resendVerificationEmail = async () => {
    if (!firebaseUser) {
      throw new Error('No hay usuario autenticado');
    }
    if (emailVerified) {
      throw new Error('El email ya está verificado');
    }
    // Enviar email de verificación usando Resend (Cloud Function)
    const sendVerificationEmailFn = httpsCallable(functions, 'sendVerificationEmail');
    await sendVerificationEmailFn({});
  };

  const refreshEmailVerification = async (): Promise<boolean> => {
    if (!firebaseUser) {
      return true;
    }
    // Verificar el estado desde la Cloud Function que lee de Firestore
    const checkEmailVerificationFn = httpsCallable<unknown, { verified: boolean }>(
      functions,
      'checkEmailVerification'
    );
    const result = await checkEmailVerificationFn({});
    const verified = result.data.verified;
    setEmailVerified(verified);
    return verified;
  };

  const dismissWelcomeModal = async () => {
    // Close modal
    setShowWelcomeModal(false);

    // Mark as shown in Firestore (both old and new flags for compatibility)
    if (firebaseUser) {
      try {
        await setDoc(doc(db, 'users', firebaseUser.uid), {
          welcomePointsShown: true,  // Old system (backward compatibility)
          welcomeStampShown: true,   // New system
          updatedAt: Timestamp.now(),
        }, { merge: true });
      } catch (error) {
        console.error('Error marking welcome modal as shown:', error);
      }
    }
  };

  const dismissGuestNameModal = () => {
    setShowGuestNameModal(false);
  };

  // Local-only patch of the user's contact fields. The Firestore write is done by
  // the caller (the phone prompt's GuestNameModal); this just keeps the in-memory
  // user in sync so a returning user is not asked again in the same session.
  const updateUserContact = (partial: { displayName?: string; phoneNumber?: string }) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  };

  const updateGuestName = async (name: string, phone: string) => {
    if (!firebaseUser) return;

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedName || !trimmedPhone) return;

    // Save name to localStorage for persistence across sessions
    localStorage.setItem('guestName', trimmedName);

    // Update Firestore user document
    try {
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        displayName: trimmedName,
        phoneNumber: trimmedPhone,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      // Update local user state
      setUser(prev => prev ? { ...prev, displayName: trimmedName, phoneNumber: trimmedPhone } : null);

      // Close modal
      setShowGuestNameModal(false);
    } catch (error) {
      console.error('Error updating guest name:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    firebaseUser,
    user,
    customClaims,
    loading,
    emailVerified,
    signInWithGoogle,
    signInWithApple,
    signInAsGuest,
    signInWithEmail,
    signUpWithEmail,
    upgradeGuestAccount,
    upgradeGuestAccountWithApple,
    signOut,
    linkGoogleAccount,
    unlinkGoogleAccount,
    updateUserFranchise,
    resendVerificationEmail,
    refreshEmailVerification,
    // Welcome stamp modal
    showWelcomeModal,
    dismissWelcomeModal,
    // Guest name modal
    showGuestNameModal,
    dismissGuestNameModal,
    updateGuestName,
    updateUserContact,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
}
